import React, { useState, useCallback } from "react";
import { useQuery } from "react-query";
import { useParams, useHistory, Link } from "react-router-dom";
import { useNotificationContext } from "contexts";
import apiService from "services/api";
import { FormGroup, Label, Input } from "reactstrap";
import { Controller, useForm } from "react-hook-form";

import { FormSelect, FormEntry, Backdrop } from "components";
import { FiLoader } from "react-icons/fi";

const initialState = {
  username: "",
  email: "",
  roleId: "",
  password: "",
  fullName: "",
  birthday: "",
  officialStartDate: "",
  personnelId: "",
  status: "Probation",
  employeeType: "Part Time",
  annualLeaveDaysCount: 0,
  compensationLeaveHoursCount: 0,
  sickLeaveDaysCount: 0,
  unpaidLeaveDaysCount: 0,
  residingIsland: "",
  hourlyRate: 0,
  rateEffectiveDate: "",
  ictTestCompletionDate: "",
  legacyTotalTeachingHours: 0,
  rfidNumber: "",
};

const statusTypes = [
  { value: "Post Probation", label: "Post Probation" },
  { value: "Probation", label: "Probation" },
  { value: "Trial Teaching", label: "Trial Teaching" },
];
const employeeTypes = [
  { value: "PT", label: "Part Time" },
  { value: "FT", label: "Full Time" },
];
const regions = [
  { value: "None", label: "None" },
  { value: "HK-Island", label: "HK-Island" },
  { value: "New Territories / Kowloon", label: "New Territories / Kowloon" },
];

const EditStaffPage = () => {
  const { staff_id } = useParams();
  const history = useHistory();
  const { createNotification } = useNotificationContext();
  const [defaultState, setDefaultState] = useState({});

  const {
    data: roles,
    isLoading: isRolesLoading,
    isFetching: isFetchingRoles,
  } = useQuery("role", () => apiService.role.get());

  const { isLoading: isLoadingStaff, data } = useQuery(
    ["staff", staff_id, "min"],
    () => apiService.staff.min(staff_id),
    {
      refetchOnwindowFocus: false,
      onSuccess: (data) => {
        const formState = { ...data, roleId: data?.role?.id };
        reset(formState);
        setDefaultState(formState);
      },
      enabled: !isRolesLoading && !isFetchingRoles,
    },
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: initialState,
  });

  const [showPassword, setShowPassword] = useState(false);

  const submitEditStaffForm = (data) => {
    apiService.staff
      .update(staff_id, data)
      .then(() => {
        createNotification({
          type: "success",
          message: "Successfully updated staff",
        });
        history.push("/staff");
      })
      .catch((error) =>
        createNotification({
          type: "danger",
          message: error.message,
        }),
      );
  };

  const resetFields = useCallback(() => reset(defaultState), [defaultState]);

  return (
    <div className="content">
      <form onSubmit={handleSubmit(submitEditStaffForm)} id="parentForm">
        <div className="d-flex justify-content-center card">
          <Backdrop open={isLoadingStaff} fitToParent={true}>
            <div className="text-center position-absolute">
              <FiLoader size={40} className="animate-spin" />
            </div>
          </Backdrop>
          <div className="card-body p-sm-5">
            <div className="d-flex justify-content-between mb-4">
              <Link to={`/staff/${staff_id}`}>
                <button className="btn btn-sm btn-info" type="button">
                  &lt; Back
                </button>
              </Link>
              <div>
                <button className="btn btn-success mx-1 btn-sm" type="submit">
                  Submit
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  onClick={resetFields}
                >
                  Reset
                </button>
              </div>
            </div>
            <div className="text-center mb-4 display-5 font-weight-bold text-white">
              Edit Staff
            </div>

            <FormSelect
              formLabel="Active"
              register={register}
              options={[
                { value: true, label: "True" },
                { value: false, label: "False" },
              ]}
              error={errors.status}
              name="active"
              disabled={isLoadingStaff}
              required
            />
            <FormEntry
              type="text"
              placeholder="Username"
              formLabel="Username"
              name="username"
              register={register}
              required
            />
            <FormEntry
              type="text"
              placeholder="Phone Number"
              formLabel="Phone Number"
              name="phoneNumber"
              register={register}
              required
            />
            <FormEntry
              type="text"
              placeholder="Personnel's ID"
              formLabel="Personnel ID"
              name="personnelId"
              register={register}
            />
            <FormEntry
              type="email"
              placeholder="Staff's email"
              formLabel="Email"
              name="email"
              register={register}
            />

            <div className="d-flex">
              <FormEntry
                type={!showPassword ? "password" : "text"}
                name="password"
                formLabel="Password"
                register={register}
                placeholder="Staff Account's Password"
              />
              <FormGroup check className="ml-3">
                <Label className="form-check-label">
                  <Input
                    className="form-check-input"
                    type="checkbox"
                    onChange={() => setShowPassword((prevState) => !prevState)}
                    value={showPassword}
                  />
                  Show
                  <span className="form-check-sign">
                    <span className="check"></span>
                  </span>
                </Label>
              </FormGroup>
            </div>

            <FormEntry
              type="text"
              placeholder="Staff's Full Name"
              formLabel="Full Name"
              name="fullName"
              register={register}
              required
            />

            <FormEntry
              type="date"
              formLabel="Birthday"
              name="birthday"
              register={register}
              required
            />

            <FormEntry
              type="date"
              formLabel="Official Start Date"
              name="officialStartDate"
              register={register}
              required
            />

            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <label className="input-group-text pr-2" htmlFor="Role">
                  Role
                </label>
              </div>
              <Controller
                name="roleId"
                control={control}
                rules={{ required: true }}
                defaultValue={data?.role?.id ?? null}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="form-control custom-select"
                    value={value}
                    onChange={onChange}
                    disabled={isRolesLoading || !data}
                    required
                  >
                    {!isRolesLoading &&
                      roles.map((role, key) => (
                        <option key={key} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                  </select>
                )}
              />
            </div>

            <FormSelect
              formLabel="Status"
              register={register}
              options={statusTypes}
              error={errors.status}
              name="status"
              disabled={isLoadingStaff}
              required
            />

            <FormSelect
              formLabel="Employee Type"
              register={register}
              options={employeeTypes}
              error={errors.employeeType}
              name="employeeType"
              disabled={isLoadingStaff}
              required
            />

            {watch("employeeType") === "PT" && (
              <div>
                <FormSelect
                  formLabel="Residing Island"
                  register={register}
                  options={regions}
                  error={errors.status}
                  name="residingIsland"
                  disabled={isLoadingStaff}
                />

                <FormEntry
                  type="number"
                  formLabel="Hourly Rate"
                  register={register}
                  error={errors.status}
                  name="hourlyRate"
                  disabled={isLoadingStaff}
                />

                <FormEntry
                  type="date"
                  formLabel="Rate Effective Date"
                  register={register}
                  error={errors.status}
                  name="rateEffectiveDate"
                  disabled={isLoadingStaff}
                />
              </div>
            )}

            <FormEntry
              type="date"
              formLabel="ICT Test Completion Date"
              register={register}
              error={errors.status}
              name="ictTestCompletionDate"
              disabled={isLoadingStaff}
            />
            <FormEntry
              type="number"
              formLabel="Old Total Teaching Hours"
              register={register}
              error={errors.status}
              step="0.25"
              name="legacyTotalTeachingHours"
              disabled={isLoadingStaff}
            />

            <FormSelect
              formLabel="Region"
              register={register}
              options={regions}
              error={errors.status}
              name="region"
              disabled={isLoadingStaff}
              required
            />

            <FormEntry
              type="number"
              formLabel="Annual Leaves"
              name="annualLeaveDaysCount"
              register={register}
              required
            />

            <FormEntry
              type="number"
              formLabel="Compensation Leaves"
              name="compensationLeaveHoursCount"
              register={register}
              required
            />

            <FormEntry
              type="number"
              formLabel="Sick Leaves"
              name="sickLeaveDaysCount"
              register={register}
              required
            />
            <FormEntry
              type="number"
              formLabel="Unpaid Leaves"
              name="unpaidLeaveDaysCount"
              register={register}
              required
            />
            <FormEntry
              type="text"
              formLabel="RFID Number"
              name="rfidNumber"
              register={register}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditStaffPage;
